import {FC, useEffect, useRef, useState} from 'react'
import {api, ChatMessage, ApiError} from '../services/api'
import {Spinner} from './Spinner'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const fmt = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'short'})
}

/**
 * Two-way conversation on a request. `mySide` decides which bubbles align right
 * (the viewer's own messages, in green) vs left (the other party, in grey).
 */
export const RequestChat: FC<{requestId: string; mySide: 'RENTER' | 'ADMIN'}> = ({requestId, mySide}) => {
  const [messages, setMessages] = useState<ChatMessage[] | null>(null)
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)

  const scroll = () => endRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'})

  useEffect(() => {
    api.requestMessages(requestId).then((m) => {setMessages(m); setTimeout(scroll, 0)}).catch(() => setMessages([]))
  }, [requestId])

  const send = async () => {
    const text = body.trim()
    if (!text) return
    setSending(true)
    try {
      const msg = await api.sendRequestMessage(requestId, text)
      setMessages((list) => [...(list ?? []), msg])
      setBody('')
      setTimeout(scroll, 0)
    } catch (e) {
      // keep the text so the user can retry
      if (e instanceof ApiError) console.warn(e.message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <div style={{fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10}}>Conversation</div>

      <div style={{maxHeight: 420, minHeight: 200, overflowY: 'auto', background: '#f6f8fb', borderRadius: 12, padding: '14px', marginBottom: 12}}>
        {messages === null ? (
          <div style={{textAlign: 'center', color: MUTED, fontSize: 13, padding: '8px 0'}}>Loading…</div>
        ) : messages.length === 0 ? (
          <div style={{textAlign: 'center', color: MUTED, fontSize: 13, padding: '8px 0'}}>No messages yet. Start the conversation below.</div>
        ) : (
          <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
            {messages.map((m) => {
              const mine = m.authorRole === mySide
              return (
                <div key={m.id} style={{display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start'}}>
                  <div style={{maxWidth: '78%'}}>
                    <div style={{
                      background: mine ? ACCENT : '#fff',
                      color: mine ? '#fff' : INK,
                      border: mine ? 'none' : '1px solid #e5e7eb',
                      borderRadius: 14,
                      borderBottomRightRadius: mine ? 4 : 14,
                      borderBottomLeftRadius: mine ? 14 : 4,
                      padding: '9px 13px',
                      fontSize: 14,
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      {m.body}
                    </div>
                    <div style={{fontSize: 11, color: MUTED, marginTop: 3, textAlign: mine ? 'right' : 'left'}}>
                      {mine ? 'You' : `${m.authorName} (${m.authorRole === 'ADMIN' ? 'Admin' : 'Renter'})`} · {fmt(m.createdAt)}
                    </div>
                  </div>
                </div>
              )
            })}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <div style={{display: 'flex', gap: 8, alignItems: 'flex-end'}}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send()}}
          placeholder='Write a reply…'
          rows={2}
          style={{flex: 1, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box'}}
        />
        <button onClick={send} disabled={sending || !body.trim()} style={{fontWeight: 700, fontSize: 14, color: '#fff', background: sending || !body.trim() ? '#7ec79a' : ACCENT, border: 'none', borderRadius: 10, padding: '11px 20px', cursor: sending || !body.trim() ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap'}}>
          {sending ? <Spinner size={14} /> : 'Reply'}
        </button>
      </div>
    </div>
  )
}
